import React, { forwardRef } from "react";
import styles from "./Button.module.css";

/**
 * Button component that displays a customizable button with optional loading state.
 *
 * @component
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - The content to display inside the button.
 * @param {"primary"|"secondary"|"warning"|"delete"} props.variant - The visual style variant of the button.
 * @param {boolean} [props.loading=false] - If true, displays a loading indicator above the button.
 * @param {boolean} [props.disable=false] - If true, disables the button.
 * @param {React.Ref} ref - Forwarded ref for the button element.
 * @returns {JSX.Element} The rendered Button component.
 */
const Button = forwardRef(({ children, variant, shape="round", loading = false, disable = false, ...rest }, ref) => {
  return (
    <button
      ref={ref} /* 👈 Explicitly attach the forwarded ref here */
      type="button"
      {...rest}
      disabled={disable || loading || (rest && rest.disabled)}
      className={[
  styles.button,
  styles[variant],
  styles[shape],
  rest && rest.className,
]
  .filter(Boolean)
  .join(" ")}
    >
      {children}
      {loading && <span className={styles.spinner} />}
    </button>
  );
});

// Good practice: Helps React DevTools display the component name correctly
Button.displayName = "Button";

export default Button;